import { and, eq } from "drizzle-orm";
import {
  aisle,
  pantry_item,
  type PowerSyncDrizzleDB,
  shopping_item,
  shopping_list,
  shopping_list_organization,
} from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

/**
 * Devuelve un mapa nombre→aisle_id creando los pasillos que falten.
 * Usado por el editor de recetas: el usuario escribe la categoría como texto
 * libre y aquí se materializa en el catálogo `aisle` del usuario.
 */
export async function ensureAislesByNameMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  names: string[],
): Promise<Map<string, string>> {
  const wanted = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
  const result = new Map<string, string>();
  if (wanted.length === 0) {
    return result;
  }

  const existing = await db.select().from(aisle).where(eq(aisle.user_id, userId));
  const byLowerName = new Map(
    existing.map((row) => [(row.name ?? "").trim().toLowerCase(), row] as const),
  );

  const now = nowISO();
  let nextOrder = existing.length;
  for (const name of wanted) {
    const found = byLowerName.get(name.toLowerCase());
    if (found) {
      result.set(name, found.id);
      continue;
    }
    const id = generateUUID();
    await db.insert(aisle).values({
      id,
      user_id: userId,
      name,
      sort_order: nextOrder,
      created_at: now,
      updated_at: now,
    });
    nextOrder += 1;
    result.set(name, id);
  }
  return result;
}

export async function createShoppingListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  weekStart?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(shopping_list).values({
    id,
    user_id: userId,
    name: name.trim(),
    week_start: weekStart ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteShoppingListMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(shopping_item).where(eq(shopping_item.shopping_list_id, listId));
    await trx
      .delete(shopping_list_organization)
      .where(eq(shopping_list_organization.shopping_list_id, listId));
    await trx.delete(shopping_list).where(eq(shopping_list.id, listId));
  });
}

/**
 * Añade un ítem manual. `ownerId` debe ser el user_id del DUEÑO de la lista
 * (denormalización del modelo de sync — el caller lo lee de la propia lista).
 */
export async function addManualItemMutation(
  db: PowerSyncDrizzleDB,
  ownerId: string,
  listId: string,
  input: {
    name: string;
    quantity?: number | null;
    unit?: string | null;
    category?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(shopping_item).values({
    id,
    shopping_list_id: listId,
    user_id: ownerId,
    name: input.name.trim(),
    quantity: input.quantity ?? null,
    unit: input.unit?.trim() || null,
    category: input.category?.trim() || null,
    checked: 0,
    is_manual: 1,
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function setItemCheckedMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  checked: boolean,
): Promise<void> {
  await db
    .update(shopping_item)
    .set({ checked: checked ? 1 : 0, updated_at: nowISO() })
    .where(eq(shopping_item.id, itemId));
}

export async function updateShoppingItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  updates: Partial<{
    name: string;
    quantity: number | null;
    unit: string | null;
    category: string | null;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await db
    .update(shopping_item)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(shopping_item.id, itemId));
}

export async function deleteShoppingItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db.delete(shopping_item).where(eq(shopping_item.id, itemId));
}

/** Elimina los ítems ya comprados de una lista. */
export async function clearCheckedItemsMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
): Promise<void> {
  await db
    .delete(shopping_item)
    .where(and(eq(shopping_item.shopping_list_id, listId), eq(shopping_item.checked, 1)));
}

/** Comparte la lista con una organización (hogar). */
export async function shareShoppingListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  listId: string,
  organizationId: string,
): Promise<void> {
  await db.insert(shopping_list_organization).values({
    id: generateUUID(),
    shopping_list_id: listId,
    organization_id: organizationId,
    added_at: nowISO(),
    added_by: userId,
  });
}

/** Revoca el compartido de la lista con una organización. */
export async function revokeShoppingListMutation(
  db: PowerSyncDrizzleDB,
  listId: string,
  organizationId: string,
): Promise<void> {
  await db
    .delete(shopping_list_organization)
    .where(
      and(
        eq(shopping_list_organization.shopping_list_id, listId),
        eq(shopping_list_organization.organization_id, organizationId),
      ),
    );
}

// ─── Despensa ────────────────────────────────────────────────────────────────

export async function addPantryItemMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: { name: string; quantity?: number | null; unit?: string | null },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(pantry_item).values({
    id,
    user_id: userId,
    name: input.name.trim(),
    quantity: input.quantity ?? null,
    unit: input.unit?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deletePantryItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db.delete(pantry_item).where(eq(pantry_item.id, itemId));
}
